import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import { Link, useLocation } from "react-router-dom";
import GuideGraph from "../../components/guideGraph/guideGraph";
import ProductLanguageGraph from "../../components/productLanguageGraph/ProductLanguageGraph";
import "./productDetails.css";

const starIcons = (rating) => {
  return Array(5)
    .fill()
    .map((_, i) => (
      <span key={i} className={i < rating ? "star-filled" : "star-empty"}>
        ★
      </span>
    ));
};

const platformColors = {
  GYG: "#F1866A",
  Headout: "#844BF6",
  Tiqets: "#6AA84F",
  Viator: "#2A6B6D",
  Civitatis: "#E91E63",
  Airbnb: "#D3D3D3",
};

const ProductDetails = () => {
  const { productCode } = useParams();
  const reviews = useSelector((state) => state.reviews.reviews);
  const { search } = useLocation();

  // Filter states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [sortOption, setSortOption] = useState("highToLow");
  const [selectedGuides, setSelectedGuides] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [selectedReviewLanguage, setSelectedReviewLanguage] = useState("All");
  const [selectedGraphLanguage] = useState("All");
  const currentYear = moment().year().toString();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedVariant, setSelectedVariant] = useState(productCode);

  useEffect(() => {
  setSelectedVariant(productCode);
}, [productCode]);


  // Product codes that have multiple time-based options
  const ProductCodesWithOptions = ["UKLO050", "FRPA049"];

  const getProductOptions = useCallback(() => {
  if (!ProductCodesWithOptions.includes(productCode)) return [];

  const optionSet = new Set();

  reviews.forEach((review) => {
    if (review[6] === productCode) {
      const code = review[8];
      if (
        code &&
        typeof code === "string" &&
        code.startsWith(productCode) &&
        code !== productCode
      ) {
        optionSet.add(code);
      }
    }
  });

  return Array.from(optionSet);
}, [reviews, productCode]);

  // Available platforms for filtering
  const availablePlatforms = [
    { code: "GYG", name: "GYG" },
    { code: "HEA", name: "Headout" },
    { code: "TIQ", name: "Tiqets" },
    { code: "VIATOR", name: "Viator" },
    { code: "CIV", name: "Civitatis" },
    { code: "ABNB", name: "Airbnb" },
  ];

  // Platform filtering logic based on product code
  const categorizePlatform = (review) => {
    const productCodeStr = String(review[1]);
    if (productCodeStr.startsWith("GYG")) {
      return "GYG";
    } else if (productCodeStr.startsWith("HEA")) {
      return "HEA";
    } else if (productCodeStr.includes("TIQ")) {
      return "TIQ";
    } else if (productCodeStr.startsWith("CIV")) {
      return "CIV";
    } else if (productCodeStr.startsWith("ABNB")) {
      return "ABNB";
    } else {
      return "VIATOR";
    }
  };

  const calculateTotalAverageVariant = useCallback(
  (code) => {
    const variantReviews =
      code === productCode
        ? reviews.filter((rev) => rev[6] === productCode)
        : reviews.filter((rev) => rev[8] === code);

    if (variantReviews.length === 0) return 0;

    const avg =
      variantReviews.reduce((sum, rev) => sum + rev[4], 0) /
      variantReviews.length;

    return avg.toFixed(2);
  },
  [reviews, productCode]
);


  // Calculate current and previous month averages with week delay for month change
  const calculateMonthlyAveragesVariant = useCallback(
  (code) => {
    const today = moment();
    const currentDate = today.date();
    const adjustedCurrent =
      currentDate <= 7 ? moment().subtract(1, "month") : moment();

    const currentMonth = adjustedCurrent.month();
    const currentYear = adjustedCurrent.year();

    const prevDate = moment(adjustedCurrent).subtract(1, "month");
    const prevMonth = prevDate.month();
    const prevYear = prevDate.year();

    const variantReviews =
      code === productCode
        ? reviews.filter((rev) => rev[6] === productCode)
        : reviews.filter((rev) => rev[8] === code);

    const cur = variantReviews.filter((rev) => {
      const d = moment(rev[3], "DD/MM/YYYY");
      return d.month() === currentMonth && d.year() === currentYear;
    });

    const prev = variantReviews.filter((rev) => {
      const d = moment(rev[3], "DD/MM/YYYY");
      return d.month() === prevMonth && d.year() === prevYear;
    });

    const currentAvg =
      cur.length === 0
        ? 0
        : cur.reduce((sum, r) => sum + r[4], 0) / cur.length;

    const prevAvg =
      prev.length === 0
        ? 0
        : prev.reduce((sum, r) => sum + r[4], 0) / prev.length;

    return {
      currentAvg,
      prevAvg,
      monthName: adjustedCurrent.format("MMMM"),
    };
  },
  [reviews, productCode]
);

  //   const currentMonthAverage =
  //     currentMonthReviews.length > 0
  //       ? currentMonthReviews.reduce((sum, review) => sum + review[4], 0) /
  //         currentMonthReviews.length
  //       : 0;

  //   const previousMonthAverage =
  //     previousMonthReviews.length > 0
  //       ? previousMonthReviews.reduce((sum, review) => sum + review[4], 0) /
  //         previousMonthReviews.length
  //       : 0;

  //   // Return the month name for display along with averages
  //   return {
  //     currentMonthAverage,
  //     previousMonthAverage,
  //     monthName: adjustedCurrent.format("MMMM"),
  //   };
  // };

  // const { currentMonthAverage, previousMonthAverage, monthName } =
  //   calculateMonthlyAverages();

  // Calculate average rating for platforms in the current month
  const calculatePlatformAveragesVariant = useCallback(
  (code) => {
    const today = moment();
    const currentDate = today.date();
    const adjustedCurrent =
      currentDate <= 7 ? moment().subtract(1, "month") : moment();

    const currentMonth = adjustedCurrent.month();
    const currentYear = adjustedCurrent.year();

    return availablePlatforms.map((platform) => {
      const variantReviews =
        code === productCode
          ? reviews.filter((rev) => rev[6] === productCode)
          : reviews.filter((rev) => rev[8] === code);

      const platformReviews = variantReviews.filter((rev) => {
        const matchesPlatform = categorizePlatform(rev) === platform.code;

        const d = moment(rev[3], "DD/MM/YYYY");
        const matchesMonth =
          d.month() === currentMonth && d.year() === currentYear;

        return matchesPlatform && matchesMonth;
      });

      const avg =
        platformReviews.length === 0
          ? 0
          : platformReviews.reduce((s, r) => s + r[4], 0) /
            platformReviews.length;

      return { ...platform, average: avg };
    });
  },
  [reviews, productCode]
);


  // Calculate percentage change
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return 0; // Avoid division by zero
    return (((current - previous) / previous) * 100).toFixed(2);
  };

  const availableLanguages = useMemo(() => {
    const excludedLanguages = ["?", "dutch"];
    const languages = new Set(
      reviews
        .map((review) => review[7]?.trim().toLowerCase())
        .filter((lang) => lang && !excludedLanguages.includes(lang))
    );

    return [
      "All",
      ...Array.from(languages)
        .map((lang) => lang.charAt(0).toUpperCase() + lang.slice(1))
        .sort(),
    ];
  }, [reviews]);

  const availableYears = useMemo(() => {
    const years = new Set();

    reviews.forEach((review) => {
      const reviewDate = moment(review[3], "D/M/YYYY");
      if (reviewDate.isValid()) {
        years.add(reviewDate.year().toString());
      }
    });

    return ["All", ...Array.from(years).sort((a, b) => b - a)]; // sort descending
  }, [reviews]);

  // Calculate guide statistics (name, average rating, and review count)
  const calculateGuideStatistics = useCallback(() => {
    const excludedGuideNames = ["?", "guide not found"];
    let filteredReviews = reviews.filter((review) => review[6] === productCode);

    // Filter by language
    if (selectedLanguage !== "All") {
      const normalizedSelectedLanguage = selectedLanguage.trim().toLowerCase();
      filteredReviews = filteredReviews.filter((review) => {
        const reviewLanguage = review[7]?.trim().toLowerCase();
        return reviewLanguage && reviewLanguage === normalizedSelectedLanguage;
      });
    }

    // Filter by year
    if (selectedYear !== "All") {
      filteredReviews = filteredReviews.filter((review) => {
        const reviewDate = moment(review[3], "D/M/YYYY"); // parse review date
        return (
          reviewDate.isValid() && reviewDate.year().toString() === selectedYear
        );
      });
    }

    const guideStats = {};

    filteredReviews.forEach((review) => {
      const guideName = review[10]?.trim();
      if (!guideName || excludedGuideNames.includes(guideName.toLowerCase()))
        return;

      const reviewDate = moment(review[3], "D/M/YYYY");
      const monthYear = reviewDate.format("MMM 'YY").trim();

      if (!guideStats[guideName]) {
        guideStats[guideName] = {
          totalRating: 0,
          reviewCount: 0,
          monthlyRatings: {},
        };
      }

      guideStats[guideName].totalRating += review[4];
      guideStats[guideName].reviewCount += 1;

      if (!guideStats[guideName].monthlyRatings[monthYear]) {
        guideStats[guideName].monthlyRatings[monthYear] = {
          totalRating: 0,
          reviewCount: 0,
        };
      }

      guideStats[guideName].monthlyRatings[monthYear].totalRating += review[4];
      guideStats[guideName].monthlyRatings[monthYear].reviewCount += 1;
    });

    return Object.keys(guideStats).map((guideName) => ({
      name: guideName,
      averageRating: (
        guideStats[guideName].totalRating / guideStats[guideName].reviewCount
      ).toFixed(2),
      reviewCount: guideStats[guideName].reviewCount,
      monthlyRatings: Object.keys(guideStats[guideName].monthlyRatings).map(
        (monthYear) => ({
          month: monthYear,
          averageRating: (
            guideStats[guideName].monthlyRatings[monthYear].totalRating /
            guideStats[guideName].monthlyRatings[monthYear].reviewCount
          ).toFixed(2),
          reviewCount:
            guideStats[guideName].monthlyRatings[monthYear].reviewCount,
        })
      ),
    }));
  }, [reviews, productCode, selectedLanguage, selectedYear]);

  let guideStatistics = useMemo(
    () => calculateGuideStatistics(),
    [calculateGuideStatistics]
  );

  useEffect(() => {
    setSelectedGuides([]);
  }, [productCode]);

  const handleGuideDrop = (newGuide) => {
    setSelectedGuides((prev) => [...prev, newGuide]);
  };

  if (sortOption === "Sort A - Z") {
    guideStatistics = guideStatistics.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  } else if (sortOption === "lowToHigh") {
    guideStatistics = guideStatistics.sort(
      (a, b) => a.averageRating - b.averageRating
    );
  } else if (sortOption === "highToLow") {
    guideStatistics = guideStatistics.sort(
      (a, b) => b.averageRating - a.averageRating
    );
  }

  // const percentageChange = calculatePercentageChange(
  //   currentMonthAverage,
  //   previousMonthAverage
  // );
  // const isPositive = percentageChange > 0;

  // Filter function
  const filterReviews = () => {
    return Array.isArray(reviews)
      ? reviews
          .filter((review) => {
  return selectedVariant === productCode
    ? review[6] === productCode
    : review[8] === selectedVariant;
}) // Filter by product code
          .filter((review) => {
            const reviewDate = moment(review[3], "DD/MM/YYYY");

            const isWithinDateRange =
              fromDate && toDate
                ? reviewDate.isBetween(
                    moment(fromDate, "YYYY-MM-DD"),
                    moment(toDate, "YYYY-MM-DD"),
                    undefined,
                    "[]"
                  )
                : true;

            const matchesRating = selectedRating
              ? review[4] === parseInt(selectedRating)
              : true;

            const matchesPlatform = selectedPlatform
              ? categorizePlatform(review) === selectedPlatform
              : true;

            const matchesLanguage =
              selectedReviewLanguage && selectedReviewLanguage !== "All"
                ? review[7]?.trim().toLowerCase() ===
                  selectedReviewLanguage.trim().toLowerCase()
                : true;

            return (
              isWithinDateRange &&
              matchesRating &&
              matchesPlatform &&
              matchesLanguage
            );
          })
          .sort(
            (a, b) => moment(b[3], "DD/MM/YYYY") - moment(a[3], "DD/MM/YYYY")
          )
      : [];
  };

  const filteredReviews = filterReviews();

  const filterGraphReviews = () => {
    return Array.isArray(reviews)
      ? reviews.filter((review) => {
          const matchesProduct = review[6] === productCode;

          const matchesLanguage =
            selectedGraphLanguage && selectedGraphLanguage !== "All"
              ? review[7]?.trim().toLowerCase() ===
                selectedGraphLanguage.trim().toLowerCase()
              : true;

          return matchesProduct && matchesLanguage;
        })
      : [];
  };

  const graphReviews = filterGraphReviews();

 const totalAverage = calculateTotalAverageVariant(selectedVariant);
const { currentAvg, prevAvg, monthName } = calculateMonthlyAveragesVariant(selectedVariant);
const percentageChange = prevAvg === 0 ? 0 : (((currentAvg - prevAvg) / prevAvg) * 100).toFixed(2);
const platformAverages = calculatePlatformAveragesVariant(selectedVariant);
const isPositive = percentageChange > 0;


  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 10;
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);

  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredReviews.slice(
    indexOfFirstReview,
    indexOfLastReview
  );

  return (
    <div className="product-reviews">
      <div className="product-main-header">
        <h2>Product Code: {productCode}</h2>
        <button
          onClick={() =>
            window.open(
              "https://docs.google.com/spreadsheets/d/1KtvBJqkDnI1aCQaDPGXvJJg_V3NxqEORoSPVe_iaCbA/edit?gid=94071857#gid=94071857",
              "_blank"
            )
          }
        >
          Guide Rates
        </button>
      </div>
      <div className="productheader">
        <div className="productheader-data">
        <div className="profuctheader-performance">
          <h3>Performance</h3>
          {ProductCodesWithOptions.includes(productCode) && (
    <select
      className="variant-selector"
      value={selectedVariant}
      onChange={(e) => setSelectedVariant(e.target.value)}
    >
      <option value={productCode}>{productCode}</option>

      {getProductOptions().map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  )}
  </div>
          <div className="mainrating-container">
            <div className="mainrating-title">
              Total Average Rating:{" "}
              <span className="mainrating-value">{totalAverage}</span>
            </div>
          </div>
          <div className="productheader-card">
            <div className="productheader-card-total">
              <p className="rating-title">Average Rating {monthName}</p>
              <p className="rating-value">{currentAvg.toFixed(2)}</p>
              <div
                className={`rating-change ${
                  isPositive ? "positive" : "negative"
                }`}
              >
                <img
                  src={isPositive ? "/trending_up.svg" : "/trending_down.svg"}
                  alt="change arrow"
                  className="arrow-icon"
                  style={{
                    filter: isPositive
                      ? "invert(29%) sepia(92%) saturate(320%) hue-rotate(100deg) brightness(93%) contrast(88%)"
                      : "invert(29%) sepia(92%) saturate(320%) hue-rotate(0deg) brightness(93%) contrast(88%)",
                  }}
                />
                <span>{Math.abs(percentageChange)}%</span>
                <span className="comparison-text">vs previous month</span>
              </div>
            </div>
            <div className="productheader-card-platform">
              {platformAverages
                .filter((platform) => platform.average > 0) // Only show platforms with a positive average
                .map((platform) => (
                  <div key={platform.code} className="platform-item">
                    <span
                      className="platform-dot"
                      style={{ backgroundColor: platformColors[platform.name] }}
                    ></span>
                    <span>
                      {platform.name}: {platform.average.toFixed(2)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className="productheader-guide">
          <div className="guide-header">
            <h3>Guide List</h3>
            <div className="guide-header-select">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year === "All" ? "All years" : year}
                  </option>
                ))}
              </select>
              <select
                id="languageFilter"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                {[
                  ...new Set(
                    availableLanguages.map(
                      (lang) => lang?.trim().replace(/\s+/g, " ") // normalize spaces
                    )
                  ),
                ]
                  .filter((lang) => lang && lang.toLowerCase() !== "no data")
                  .map((lang) => (
                    <option key={lang} value={lang}>
                      {lang === "All" ? "All Lang" : lang}
                    </option>
                  ))}
              </select>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="Sort A - Z">Sort A - Z</option>
                <option value="highToLow">High to Low</option>
                <option value="lowToHigh">Low to High</option>
              </select>
            </div>
          </div>
          {guideStatistics.map((guide) => (
            <div
              key={guide.name}
              className="guide-item"
              draggable
              onDragStart={(e) => {
                const guideData = {
                  ...guide,
                  selectedYear,
                };
                const guideString = JSON.stringify(guideData);
                e.dataTransfer.setData("application/json", guideString);
                e.dataTransfer.setData("text/plain", guideString);
              }}
            >
              <span className="guide-name">
                <Link
                  to={{ pathname: `/guides/${encodeURIComponent(guide.name)}`, search }}
                  className="guide-link"
                >
                  {guide.name}
                </Link>
              </span>
              <span className="guide-stats">
                {guide.averageRating} Stars ({guide.reviewCount} reviews)
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="guide-graph-container">
        <GuideGraph
          selectedGuides={selectedGuides}
          onDropGuide={handleGuideDrop}
        />
      </div>
      <div className="product-language-container">
        <ProductLanguageGraph
          productCode={productCode}
          reviews={graphReviews}
          getProductOptions={getProductOptions}
        />
      </div>
      {/* Filter Controls */}
      <div className="filter-controls">
        <label>
          From Date:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label>
          To Date:
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <label>
          Rating:
          <select
            value={selectedRating}
            onChange={(e) => setSelectedRating(e.target.value)}
          >
            <option value="">All</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </label>
        <label>
          Platform:
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
          >
            <option value="">All</option>
            {availablePlatforms.map((platform) => (
              <option key={platform.code} value={platform.code}>
                {platform.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Language:
          <select
            value={selectedReviewLanguage}
            onChange={(e) => setSelectedReviewLanguage(e.target.value)}
          >
            {[
              ...new Set(
                availableLanguages.map(
                  (lang) => lang?.trim().replace(/\s+/g, " ") // normalize spaces
                )
              ),
            ]
              .filter(
                (lang) => lang && lang.toLowerCase() !== "no data" // remove no data
              )
              .map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
          </select>
        </label>
        {ProductCodesWithOptions.includes(productCode) && (
  <label>
    Option:
    <select
      value={selectedVariant}
      onChange={(e) => setSelectedVariant(e.target.value)}
    >
      <option value={productCode}>{productCode}</option>

      {getProductOptions().map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </label>
)}
      </div>

      {/* Reviews Display */}
      {currentReviews.length > 0 ? (
        currentReviews.map((review, index) => (
          <div key={index} className="productreview-card">
            <div className="productreview-header">
              <div className="productreview-id-container">
                <span className="productreview-id">{review[1]}</span>
                <span className="productreview-date">
                  Tour Date: {review[2]}
                </span>
              </div>
              <div className="productreview-star-container">
  <span className="productstars">{starIcons(review[4])}</span>

  <div className="productproduct-code-wrapper">
    <span className="productproduct-code">
      {review[6]} ({review[7]})
    </span>

    {review[8] && String(review[8]).trim() !== "" && (
      <span className="productproduct-option">
        {String(review[8])}
      </span>
    )}
  </div>
</div>
            </div>
            <p className="productcomment">{review[5]}</p>
            <div className="productreview-footer">
              <span className="productdate">Comment Date: {review[3]}</span>
              <span className="productguide-name">
                <div className="productguide-heading">Guide:</div>
                <Link
                  to={{ pathname: `/guides/${encodeURIComponent(String(review[10] ?? ''))}`, search }}
                  className="guide-link"
                >
                  {review[10]}
                </Link>
              </span>
            </div>
          </div>
        ))
      ) : (
        <p>No reviews available for this product code.</p>
      )}

      {/* Pagination Controls */}
      <div className="pagination">
        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
          &laquo;
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          &lt;
        </button>
        {currentPage > 3 && <span>...</span>} {/* Optional left ellipsis */}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pageNum = currentPage - 2 + i;
          if (pageNum < 1 || pageNum > totalPages) return null;
          return (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={currentPage === pageNum ? "active" : ""}
            >
              {pageNum}
            </button>
          );
        })}
        {currentPage < totalPages - 2 && <span>...</span>}{" "}
        {/* Optional right ellipsis */}
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
        >
          &gt;
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

export default ProductDetails;
